/**
 * Job → job links (the ticket form's Link button), as a row of chips:
 * "From #12345" for the job this one was raised from, then one chip per job
 * raised from this one. Each chip opens that ticket at `/ticket/{number}`.
 *
 * Shared by the ticket edit form header and the dashboard card's Linked Items
 * section so the two read the same. Renders nothing when there are no links.
 */

import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { IconArrowBackUp, IconLink } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { LinkedTicketSummary } from '@techgitter/contracts';
import { Chip } from '../chip';

interface LinkedTicketChipsProps {
  parent?: { number?: number; title?: string } | null;
  children: LinkedTicketSummary[];
  isLoading?: boolean;
}

export const LinkedTicketChips: React.FC<LinkedTicketChipsProps> = ({ parent, children, isLoading }) => {
  const navigate = useNavigate();
  const open = (number?: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (number) navigate(`/ticket/${number}`);
  };

  const hasParent = !!parent?.number;
  if (!hasParent && children.length === 0 && !isLoading) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {hasParent && (
        <Chip
          tone="neutral"
          onClick={open(parent!.number)}
          title={`Raised from #${parent!.number}${parent!.title ? ` — ${parent!.title}` : ''}`}
        >
          <IconArrowBackUp size={14} stroke={1.5} /> From #{parent!.number}
        </Chip>
      )}
      {children.map(child => (
        <Chip
          key={child.guid}
          tone="neutral"
          onClick={open(child.number)}
          title={`Linked ticket #${child.number ?? '?'}${child.title ? ` — ${child.title}` : ''}${child.status ? ` (${child.status})` : ''}`}
        >
          <IconLink size={14} stroke={1.5} /> #{child.number ?? '…'}
        </Chip>
      ))}
      {isLoading && <CircularProgress size={12} />}
    </Box>
  );
};
